import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { propType } from 'graphql-anywhere'
import { useDispatch } from 'react-redux'
import { useQuery } from 'react-apollo'
import { isEmpty } from 'ramda'

import Imgix from '../imgix'
import Price from './price'
import EvIframe from '../../containers/shared/evergent-iframe'
import Clamped from '../shared/clamped'

import CONTENT_ITEM_FRAGMENT from '../../../graphql/fragments/content-item.gql'
import RENTAL_PAYMENT_VIEW_QUERY from '../../../graphql/queries/rental-payment-view.gql'
import { LoadingModal } from '../loading/modal'
import {
  dataLayerAddToCart,
  dataLayerPurchaseRental
} from '../../lib/analytics/datalayer'
import { E2E_SELECTORS } from '../../e2e-selectors'
import { displayErrorDialog } from '../../actions'
import { getGQLError } from '../../lib/apollo'
import { segmentTrackInitiatePurchase } from '../../segment/segment-track'

import styles from './payment-view.css'

function PaymentView({ contentItem, onPaymentSubmission, onRequestClose }) {
  const dispatch = useDispatch()
  const [iframe, setIframe] = useState('')
  const product = contentItem.products[0]
  // fire gql rentalPaymentView
  const {
    data: rentalPaymentViewData,
    loading: rentalPaymentViewLoading,
    error: rentalPaymentViewError
  } = useQuery(RENTAL_PAYMENT_VIEW_QUERY, {
    variables: {
      contentItemId: contentItem.id,
      productId: product.id
    },
    fetchPolicy: 'network-only'
  })

  // when gql rentalPaymentView failed(e.g. the movie is already rented), display error
  useEffect(() => {
    if (!rentalPaymentViewError) return
    const errorObj = getGQLError(rentalPaymentViewError)
    dispatch(displayErrorDialog(errorObj))
    onRequestClose()
  }, [rentalPaymentViewError, onRequestClose, dispatch])

  // when gql rentalPaymentView is successful, set iframeURL
  useEffect(() => {
    if (!rentalPaymentViewData) return
    const iframeURL = rentalPaymentViewData.rentalPaymentView.iframe
    setIframe(iframeURL)
  }, [rentalPaymentViewData])

  // send Data layer and Segment event only once when the payment view is displayed
  useEffect(() => {
    if (!rentalPaymentViewData) return
    dataLayerAddToCart(
      contentItem,
      rentalPaymentViewData.rentalPaymentView.price,
      product.name
    )
    segmentTrackInitiatePurchase(null, contentItem)
  }, [contentItem, rentalPaymentViewData, product.name])

  // handle when payment in iframe is successful
  const handleIframeLoad = useCallback(
    iframeQueryParams => {
      if (!iframeQueryParams || isEmpty(iframeQueryParams)) return

      if (iframeQueryParams.status === 'success') {
        dataLayerPurchaseRental(
          contentItem,
          product.currentPrice,
          product.name,
          iframeQueryParams.orderId
        )
        onPaymentSubmission(iframeQueryParams.orderId)
      }
    },
    [contentItem, onPaymentSubmission, product]
  )

  if (rentalPaymentViewLoading || !rentalPaymentViewData) {
    return <LoadingModal />
  }
  const rentalPaymentView = rentalPaymentViewData.rentalPaymentView
  const notes = [
    rentalPaymentView.timeToBeginWatch,
    rentalPaymentView.hoursFromFirstPlay,
    rentalPaymentView.freeWithSubscription
  ].filter(note => note != null)
  const price = rentalPaymentView.price
  const header = rentalPaymentView.header
  const image = rentalPaymentView.image.uri

  return (
    <div className={styles.parentWrapper}>
      {/** content image */}
      <div className={styles.imageWrapper}>
        <Imgix
          className={styles.productImage}
          src={image}
          recipe="purchaseConfirmation"
        />
      </div>
      {/** content title and notes */}
      <div className={styles.productContentWrapper}>
        <div className={styles.productInfo}>
          <Clamped className={styles.title}>{header}</Clamped>
          <div className={styles.productOffer}>
            <Price amount={price} />
          </div>
        </div>
        <ul className={styles.productNotes}>
          {notes.map(note => {
            return (
              <li className={styles.productNote} key={note}>
                {note}
              </li>
            )
          })}
        </ul>
      </div>
      {/** iframe */}
      <div
        className={styles.iframeWrapper}
        data-lbx-e2e={E2E_SELECTORS.RENTAL_IFRAME}
      >
        <EvIframe
          className="e2e-iframe-rental"
          src={iframe}
          onLoad={handleIframeLoad}
          onCancel={() => onRequestClose()}
        />
      </div>
    </div>
  )
}

PaymentView.propTypes = {
  contentItem: propType(CONTENT_ITEM_FRAGMENT).isRequired,
  onPaymentSubmission: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired
}

export default PaymentView
